import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, Calendar, DollarSign, User, Mail, Phone, Globe, Tag, FileText,
  Users, Clock, CheckCircle, TrendingUp, Edit, Send, ExternalLink,
  Instagram, Twitter, Linkedin
} from 'lucide-react';
import { Partnership } from '../../store/partnershipsStore';
import { useContractsStore } from '../../store/contractsStore';

interface ViewPartnershipModalProps {
  isOpen: boolean;
  onClose: () => void;
  partnership: Partnership | null;
  onEdit?: () => void;
}

const ViewPartnershipModal: React.FC<ViewPartnershipModalProps> = ({
  isOpen,
  onClose,
  partnership,
  onEdit
}) => {
  const navigate = useNavigate();
  const { contracts } = useContractsStore();
  const [activeTab, setActiveTab] = useState<'details' | 'deliverables' | 'social'>('details');

  const existingContract = partnership
    ? contracts.find(c =>
      (typeof c.partnership === 'string' ? c.partnership === String(partnership.id) : c.partnership?.id === String(partnership.id)) ||
      (c as any).partnershipId === String(partnership.id) // Handle potential backend field name mismatch
    )
    : null;

  if (!isOpen || !partnership) return null;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString: string | Date | null | undefined): string => {
    if (!dateString) return '';
    try {
      const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch (error) {
      return '';
    }
  };

  const getStageStyles = (stage: string) => {
    const styles: Record<string, { bg: string; text: string }> = {
      'Lead': { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300' },
      'Pitch Sent': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400' },
      'Contract Sent': { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400' },
      'Content Delivered': { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400' },
      'Payment Received': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400' },
    };
    return styles[stage] || styles['Lead'];
  };

  const getPriorityStyles = (priority: string) => {
    const styles: Record<string, { bg: string; text: string }> = {
      'high': { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400' },
      'medium': { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400' },
      'low': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400' },
    };
    return styles[priority] || { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300' };
  };

  const getStageIcon = (stage: string) => {
    switch (stage) {
      case 'Lead': return <Users className="w-4 h-4" />;
      case 'Pitch Sent': return <Send className="w-4 h-4" />;
      case 'Contract Sent': return <FileText className="w-4 h-4" />;
      case 'Content Delivered': return <CheckCircle className="w-4 h-4" />;
      case 'Payment Received': return <DollarSign className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const stageStyles = getStageStyles(partnership.stage);
  const hasSocialMedia = partnership.socialMedia?.instagram || partnership.socialMedia?.twitter ||
    partnership.socialMedia?.linkedin || partnership.socialMedia?.tiktok;

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {partnership.brand}
                  </h2>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${stageStyles.bg} ${stageStyles.text}`}>
                    {getStageIcon(partnership.stage)}
                    {partnership.stage}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1.5">
                    <User className="w-4 h-4" />
                    {partnership.contact}
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              {onEdit && (
                <button
                  onClick={onEdit}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
              )}

              {/* Contract Button */}
              {existingContract ? (
                <button
                  onClick={() => {
                    onClose();
                    navigate('/dashboard/contracts', { state: { viewContractId: existingContract.id } });
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  View Contract
                  <span className="text-xs bg-indigo-200 dark:bg-indigo-800 px-1.5 py-0.5 rounded ml-1 capitalize">
                    {existingContract.status}
                  </span>
                </button>
              ) : (
                <button
                  onClick={() => {
                    onClose();
                    navigate('/dashboard/contracts', { state: { createForPartnership: partnership } });
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  Create Contract
                </button>
              )}

              <button
                onClick={() => {
                  onClose();
                  navigate('/dashboard/communication', { state: { composeForPartnership: partnership } });
                }}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Send Email
              </button>
              {partnership.website && (
                <a
                  href={partnership.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-purple-700 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 rounded-xl hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Website
                </a>
              )}
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 px-6 py-2 border-b border-gray-200 dark:border-gray-700">
              {(['details', 'deliverables', ...(hasSocialMedia ? ['social'] : [])] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab as any)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors capitalize ${activeTab === tab
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Details Tab */}
              {activeTab === 'details' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Key Information</h3>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                          <DollarSign className="w-4 h-4" />
                          <span className="text-xs font-medium uppercase">Value</span>
                        </div>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">
                          {formatCurrency(partnership.value)}
                        </p>
                      </div>

                      <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                          <TrendingUp className="w-4 h-4" />
                          <span className="text-xs font-medium uppercase">Priority</span>
                        </div>
                        {partnership.priority && (
                          <span className={`inline-flex px-2.5 py-1 rounded-lg text-sm font-medium capitalize ${getPriorityStyles(partnership.priority).bg} ${getPriorityStyles(partnership.priority).text}`}>
                            {partnership.priority}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Contact Info */}
                    <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Contact Details</h4>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                            <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Email</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{partnership.email}</p>
                          </div>
                        </div>
                        {partnership.phone && (
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                              <Phone className="w-4 h-4 text-green-600 dark:text-green-400" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400">Phone</p>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">{partnership.phone}</p>
                            </div>
                          </div>
                        )}
                        {partnership.website && (
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                              <Globe className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400">Website</p>
                              <a href={partnership.website} target="_blank" rel="noopener noreferrer"
                                className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline">
                                {partnership.website}
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Delivery Date */}
                    {partnership.deliveryDate && (
                      <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                            <Calendar className="w-4 h-4" />
                            <span className="text-sm">Delivery Date</span>
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatDate(partnership.deliveryDate)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    {/* Tags */}
                    {partnership.tags && partnership.tags.length > 0 && (
                      <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Tags</h4>
                        <div className="flex flex-wrap gap-2">
                          {partnership.tags.map((tag, index) => (
                            <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-lg text-sm">
                              <Tag className="w-3 h-3" />
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Campaign Type & Description */}
                    {(partnership.campaignType || partnership.campaignDescription) && (
                      <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Campaign Details</h4>
                        {partnership.campaignType && (
                          <div className="mb-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400">Type: </span>
                            <span className="text-sm text-gray-700 dark:text-gray-300">{partnership.campaignType}</span>
                          </div>
                        )}
                        {partnership.campaignDescription && (
                          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                            {partnership.campaignDescription}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Notes */}
                    {partnership.notes && (
                      <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Notes</h4>
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                          {partnership.notes}
                        </p>
                      </div>
                    )}

                    {/* Timestamps */}
                    <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                      <div className="space-y-2">
                        {partnership.createdAt && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500 dark:text-gray-400">Created</span>
                            <span className="text-gray-900 dark:text-white">{formatDate(partnership.createdAt)}</span>
                          </div>
                        )}
                        {partnership.lastUpdated && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500 dark:text-gray-400">Last Updated</span>
                            <span className="text-gray-900 dark:text-white">{formatDate(partnership.lastUpdated)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Deliverables Tab */}
              {activeTab === 'deliverables' && (
                <div className="space-y-4">
                  {partnership.deliverables && partnership.deliverables.length > 0 ? (
                    <div className="grid gap-3">
                      {partnership.deliverables.map((deliverable, index) => (
                        <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                              <CheckCircle className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-medium text-gray-900 dark:text-white">{deliverable.title}</span>
                                {deliverable.contentType && (
                                  <span className="px-2 py-0.5 text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-full">
                                    {deliverable.contentType}
                                  </span>
                                )}
                              </div>
                              {deliverable.description && (
                                <p className="text-sm text-gray-600 dark:text-gray-400">{deliverable.description}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
                        <FileText className="w-8 h-8 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No Deliverables</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        No deliverables have been defined for this partnership.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Social Tab */}
              {activeTab === 'social' && hasSocialMedia && (
                <div className="grid gap-4">
                  {partnership.socialMedia?.instagram && (
                    <a href={`https://instagram.com/${partnership.socialMedia.instagram}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-4 p-4 bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 rounded-xl hover:shadow-md transition-all">
                      <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center">
                        <Instagram className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Instagram</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">@{partnership.socialMedia.instagram}</p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400 ml-auto" />
                    </a>
                  )}
                  {partnership.socialMedia?.twitter && (
                    <a href={`https://twitter.com/${partnership.socialMedia.twitter}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl hover:shadow-md transition-all">
                      <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                        <Twitter className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Twitter</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">@{partnership.socialMedia.twitter}</p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400 ml-auto" />
                    </a>
                  )}
                  {partnership.socialMedia?.linkedin && (
                    <a href={`https://linkedin.com/in/${partnership.socialMedia.linkedin}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl hover:shadow-md transition-all">
                      <div className="w-12 h-12 bg-blue-700 rounded-xl flex items-center justify-center">
                        <Linkedin className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">LinkedIn</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{partnership.socialMedia.linkedin}</p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400 ml-auto" />
                    </a>
                  )}
                  {partnership.socialMedia?.tiktok && (
                    <a href={`https://tiktok.com/@${partnership.socialMedia.tiktok}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:shadow-md transition-all">
                      <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center">
                        <span className="text-white font-bold text-sm">TT</span>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">TikTok</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">@{partnership.socialMedia.tiktok}</p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400 ml-auto" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewPartnershipModal;